import { initializeApp, getApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  User,
  initializeAuth,
  getReactNativePersistence,
  Auth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  browserLocalPersistence,
  indexedDBLocalPersistence
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  Firestore,
  orderBy,
  limit,
  updateDoc
} from 'firebase/firestore';
import { getDatabase, Database } from 'firebase/database';
import ReactNativeAsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import { EMAIL_API_URL } from '../constants/api';

const firebaseConfig = {
  apiKey: "AIzaSyDhtX8qlRmfQuSOb7abA3b5ZLWeiaig17E",
  authDomain: "the-nust-life-475bb.firebaseapp.com",
  databaseURL: "https://the-nust-life-475bb-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "the-nust-life-475bb",
  storageBucket: "the-nust-life-475bb.firebasestorage.app",
  messagingSenderId: "363700543898",
  appId: "1:363700543898:web:beed8f7e83941109ad9a23"
};

class FirebaseService {
  private static instance: FirebaseService;
  private app;
  private auth: Auth;
  private db: Firestore;
  private rtdb: Database;
  private _initialized: boolean = false;

  private constructor() {
    try {
      this.app = getApp();
    } catch {
      this.app = initializeApp(firebaseConfig);
    }

    try {
      if (Platform.OS === 'web') {
        this.auth = initializeAuth(this.app, {
          persistence: [indexedDBLocalPersistence, browserLocalPersistence]
        });
      } else {
        this.auth = getAuth(this.app);
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      this.auth = getAuth(this.app);
    }

    this.db = getFirestore(this.app);
    this.rtdb = getDatabase(this.app);
    this._initialized = true;
  }

  static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  isInitialized(): boolean {
    return this._initialized;
  }

  // Subscribe to auth state changes
  subscribeToAuthChanges(callback: (user: User | null) => void) {
    return onAuthStateChanged(this.auth, callback);
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  public getAuth(): Auth {
    return this.auth;
  }

  public getDb(): Firestore {
    return this.db;
  }

  public getRTDB(): Database {
    return this.rtdb;
  }

  public getApp() {
    return this.app;
  }

  public async signIn(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  public async signUp(email: string, password: string, username: string): Promise<any> {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;

      // Create user document in Firestore
      await setDoc(doc(this.db, 'users', user.uid), {
        username,
        email,
        role: 'student',
        createdAt: new Date(),
        updatedAt: new Date(),
        expoPushToken: null // Will be updated when token is available
      });

      return userCredential;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }

  public async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  public getProjectId(): string {
    return firebaseConfig.projectId;
  }

  public async initialize() {
    if (this._initialized) {
      return;
    }

    try {
      // Set up Android notification channel
      if (Platform.OS === 'android') {
        await Notifications.setNotificationChannelAsync('default', {
          name: 'default',
          importance: Notifications.AndroidImportance.MAX,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#FF231F7C',
          sound: 'default'
        });
      }

      // Configure notification behavior
      await Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowAlert: true,
          shouldPlaySound: true,
          shouldSetBadge: false,
        }),
      });

      this._initialized = true;
      console.log('Firebase initialized successfully');
    } catch (error) {
      console.error('Error initializing Firebase:', error);
      throw error;
    }
  }

  // Verification code methods
  async sendVerificationCode(email: string): Promise<boolean> {
    try {
      // Generate a random 6-digit code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Store the code in Firestore with timestamp
      const verificationRef = doc(collection(this.db, 'verificationCodes'));
      await setDoc(verificationRef, {
        email,
        code,
        createdAt: new Date(),
        used: false
      });

      // Send verification email through the API
      const response = await fetch(`${EMAIL_API_URL}/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send verification email');
      }

      return true;
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw error;
    }
  }

  async verifyCode(email: string, code: string): Promise<boolean> {
    try {
      // Get verification codes for this email
      const verificationCodesRef = collection(this.db, 'verificationCodes');
      const q = query(
        verificationCodesRef,
        where('email', '==', email),
        where('code', '==', code),
        where('used', '==', false),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return false;
      }

      const docRef = snapshot.docs[0];
      const data = docRef.data();
      const createdAt = data.createdAt.toDate();
      const now = new Date();
      const timeDiff = now.getTime() - createdAt.getTime();
      const timeLimit = 10 * 60 * 1000; // 10 minutes in milliseconds

      // Check if code is expired
      if (timeDiff > timeLimit) {
        return false;
      }

      // Mark code as used
      await updateDoc(docRef.ref, { used: true });

      return true;
    } catch (error) {
      console.error('Error verifying code:', error);
      throw error;
    }
  }
}

// Export the class itself and create a default instance
export const firebaseService = FirebaseService.getInstance();
export default FirebaseService;
